import type { PanelSubscriptionPlan } from '@vaptmenu/analytics-react';
import { formatCurrency } from '@/shared/lib/money';
import type {
  BillingCycle,
  PlanCode,
  PricingInfo,
  PricingPlan,
} from '../types/billing.types';
import {
  BILLING_CYCLE_SUFFIX,
  KNOWN_PLAN_NAMES,
  PLAN_CODES,
  PLAN_FEATURE_LABELS,
} from '../types/billing.types';

/* ==========================================================================
   Planos no painel (change billing-plans)

   A lista à venda vem só de `plans[]`, que a API monta com os planos
   públicos: nenhuma tela mostra um plano que o dono não pode comprar. Sem o
   campo (API anterior), a lista fica vazia e o painel age como antes dos
   planos: sem escolha nem troca.
   ========================================================================== */

/** Os planos à venda, na ordem do catálogo; vazio numa API anterior aos planos. */
export function plansForSale(pricing: PricingInfo | undefined): PricingPlan[] {
  return pricing?.plans ?? [];
}

/** Há escolha (ao assinar) e troca (no card) só com mais de um plano à venda. */
export function hasPlanChoice(pricing: PricingInfo | undefined): boolean {
  return plansForSale(pricing).length > 1;
}

/** O plano escolhido por padrão: o Essencial, ou o primeiro da lista se ele saiu da venda. */
export function defaultPlanCode(plans: PricingPlan[]): PlanCode | undefined {
  return plans.find((plan) => plan.code === PLAN_CODES.essential)?.code ?? plans[0]?.code;
}

/** Nome do plano: o que a API mandou, o do catálogo conhecido ou, por último, o código. */
export function planDisplayName(
  code: PlanCode | null | undefined,
  plans: PricingPlan[] = [],
  apiName?: string | null,
): string | null {
  if (apiName) return apiName;
  if (!code) return null;
  return plans.find((plan) => plan.code === code)?.name ?? KNOWN_PLAN_NAMES[code] ?? code;
}

export function cyclePrice(plan: PricingPlan, cycle: BillingCycle): number {
  return cycle === 'yearly' ? plan.yearlyPrice : plan.monthlyPrice;
}

/** "R$ 39,90/mês" ou "R$ 1.499,00/ano". */
export function formatCyclePrice(value: number, cycle: BillingCycle): string {
  return `${formatCurrency(value)}${BILLING_CYCLE_SUFFIX[cycle]}`;
}

/** O que o plano inclui, lido dos recursos e da cota que a API devolve. */
export function planIncludes(plan: PricingPlan): string[] {
  const items = plan.features.map((feature) => PLAN_FEATURE_LABELS[feature] ?? feature);
  if (plan.includedAiUnitsPerMonth > 0) {
    items.push(`${plan.includedAiUnitsPerMonth.toLocaleString('pt-BR')} atendimentos por mês`);
  }
  if (items.length === 0) {
    items.push('Cardápio digital, pedidos e delivery');
  }
  return items;
}

/* ---- Plano no evento de analytics ---- */

/** Os planos que o `panel.subscription_action` aceita; um plano novo entra na taxonomia antes daqui. */
const ANALYTICS_PLANS: Record<PanelSubscriptionPlan, true> = {
  essential: true,
  attendant: true,
};

function isAnalyticsPlan(code: string): code is PanelSubscriptionPlan {
  return Object.hasOwn(ANALYTICS_PLANS, code);
}

/**
 * O plano como o evento `panel.subscription_action` o aceita. Um código fora
 * do conjunto (o `trial`, ou um plano que a API passou a vender antes de o
 * painel conhecer) não vai ao evento: a propriedade fica ausente.
 */
export function analyticsPlanOf(code: PlanCode | null | undefined): PanelSubscriptionPlan | undefined {
  return code && isAnalyticsPlan(code) ? code : undefined;
}

/* ---- Datas no calendário de São Paulo, o mesmo do Billing ---- */

const saoPauloDayMonth = new Intl.DateTimeFormat('pt-BR', {
  timeZone: 'America/Sao_Paulo',
  day: '2-digit',
  month: '2-digit',
});

/** "DD/MM" de um instante, em São Paulo. */
export function formatDayMonth(instant: string): string {
  return saoPauloDayMonth.format(new Date(instant));
}

/**
 * Último dia ("DD/MM") de algo que termina num instante exclusivo, como a
 * cortesia: o dia de um instante antes do fim. O backoffice grava a cortesia
 * "válida até 31/12" com fim à meia-noite de 01/01.
 */
export function formatLastDay(exclusiveEnd: string): string {
  return saoPauloDayMonth.format(new Date(Date.parse(exclusiveEnd) - 1));
}

/** "DD/MM" de uma data sem hora ("AAAA-MM-DD"), sem passar por fuso. */
export function formatDateOnlyDayMonth(date: string): string {
  const [, month, day] = date.slice(0, 10).split('-');
  return day && month ? `${day}/${month}` : date;
}
